import { useRefreshSuperAdminStorefrontEmbed } from "@/api/super-admin/use-super-admin-storefront-embed";
import type { StorefrontEmbedAuditDto } from "@babackapp/private-api-client/generated/schema";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { StorefrontEmbedDeleteDialog } from "./storefront-embed-delete-dialog";
import type { EmbedShop } from "./storefront-embed-row-filter";
import { isReadyToClean } from "./storefront-embed-summary";

const STATUS_LABELS = {
  active: "Actif",
  inactive: "Inactif",
  unknown: "Inconnu",
} as const;

const STATUS_STYLES = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-amber-100 text-amber-800",
  unknown: "bg-muted text-muted-foreground",
} as const;

const deleteBlockedReason = (audit: StorefrontEmbedAuditDto): string => {
  if (audit.embedStatus !== "active") {
    return "L'app embed n'est pas active : suppression impossible";
  }
  return "Aucun script tag Baback à supprimer";
};

export function StorefrontEmbedShopRow({
  shop,
  audit,
  hasLoadFailed,
}: {
  shop: EmbedShop;
  audit: StorefrontEmbedAuditDto | undefined;
  hasLoadFailed: boolean;
}) {
  const refresh = useRefreshSuperAdminStorefrontEmbed(shop.id);
  const canDelete = audit !== undefined && isReadyToClean(audit);

  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="px-3 py-2">
        <div className="font-medium">{shop.name}</div>
        <div className="text-xs text-muted-foreground">{shop.shopifyUrl}</div>
      </td>
      <td className="px-3 py-2">
        {audit ? (
          <>
            <Badge
              variant="outline"
              className={STATUS_STYLES[audit.embedStatus]}
            >
              {STATUS_LABELS[audit.embedStatus]}
            </Badge>
            {audit.error && (
              <div
                className="mt-1 text-xs text-destructive"
                title={audit.error}
              >
                {audit.error}
              </div>
            )}
          </>
        ) : (
          <span className="text-xs text-muted-foreground">
            {hasLoadFailed ? "Erreur de chargement" : "Chargement…"}
          </span>
        )}
        {refresh.error && (
          <div className="mt-1 text-xs text-destructive">
            {refresh.error.message}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {audit && audit.babackScriptTags.length > 0 ? (
          <details>
            <summary className="cursor-pointer tabular-nums">
              {audit.babackScriptTags.length}
            </summary>
            <ul className="mt-1 max-w-md space-y-1 break-all text-xs text-muted-foreground">
              {audit.babackScriptTags.map((tag) => (
                <li key={tag.id}>{tag.src}</li>
              ))}
            </ul>
          </details>
        ) : (
          audit && <span className="tabular-nums">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {audit &&
          formatDistanceToNow(new Date(audit.checkedAt), {
            addSuffix: true,
            locale: fr,
          })}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            {refresh.isPending ? "Rafraîchissement…" : "Rafraîchir"}
          </Button>
          {audit?.activationUrl && (
            <Button size="sm" variant="outline" asChild>
              <a href={audit.activationUrl} target="_blank" rel="noreferrer">
                Lien d&apos;activation
              </a>
            </Button>
          )}
          {audit && canDelete ? (
            <StorefrontEmbedDeleteDialog shop={shop} audit={audit}>
              <Button size="sm" variant="destructive">
                Supprimer les script tags
              </Button>
            </StorefrontEmbedDeleteDialog>
          ) : (
            audit && (
              <span title={deleteBlockedReason(audit)}>
                <Button size="sm" variant="destructive" disabled>
                  Supprimer les script tags
                </Button>
              </span>
            )
          )}
        </div>
      </td>
    </tr>
  );
}
